import React, { useState, useCallback } from 'react';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const INITIAL_FORM = {
  name: '', partner: '', email: '', phone: '', city: '', date: '',
  guests: '', budget: '', destination: '', support: '', message: '', website: '',
};

const SUNDAY_MIRROR_INBOX = 'info@sundaymirror.in';
const SUNDAY_MIRROR_WHATSAPP = '919567655569';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function validateForm(form) {
  if (!form.name || !form.email) {
    return { ok: false, error: 'Please share your name and email so we can respond.' };
  }
  return { ok: true };
}

function buildWhatsApp(form) {
  const lines = [
    '*New Wedding Enquiry*',
    `Name: ${form.name}`,
    `Partner's Name: ${form.partner || '—'}`,
    `Email: ${form.email}`,
    `Phone / WhatsApp: ${form.phone || '—'}`,
    `Currently living in: ${form.city || '—'}`,
    `Preferred Wedding Date: ${form.date || '—'}`,
    `Estimated Guest Count: ${form.guests || '—'}`,
    `Wedding Budget Range: ${form.budget || '—'}`,
    `Destination in mind: ${form.destination || '—'}`,
    `Planning support required: ${form.support || '—'}`,
    `Message: ${form.message || '—'}`,
  ];
  return `https://wa.me/${SUNDAY_MIRROR_WHATSAPP}?text=${encodeURIComponent(lines.join('\n'))}`;
}

function Field({ label, value, onChange, type = 'text', textarea = false, testId }) {
  const base = 'bg-transparent border-b border-[#0b5260]/30 focus:border-[#c3a66d] outline-none py-3 text-[#053e49] font-serif text-lg placeholder:text-[#6b7c80]/60';
  return (
    <div className="flex flex-col">
      <label className="eyebrow text-[#a6894f] mb-2">{label}</label>
      {textarea ? (
        <textarea data-testid={testId} rows={4} value={value} onChange={onChange} className={`${base} resize-none`} />
      ) : (
        <input data-testid={testId} type={type} value={value} onChange={onChange} className={base} />
      )}
    </div>
  );
}

export default function ContactForm({ compact = false }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);

  const change = useCallback((key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
  }, []);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    const { ok, error } = validateForm(form);
    if (!ok) { toast({ title: error }); return; }

    const waWindow = window.open(buildWhatsApp(form), '_blank');
    if (waWindow) waWindow.opener = null;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, form);
      toast({ title: data.email_status === 'sent'
        ? `Thank you — your enquiry has been sent to ${SUNDAY_MIRROR_INBOX}. Tap send in WhatsApp to message us too.`
        : 'Thank you — your enquiry has been received. Please tap send in WhatsApp so we can reach you quickly.' });
      setForm(INITIAL_FORM);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast({ title: Array.isArray(detail) ? 'Please check your email address and try again.' : (detail || 'We could not send your enquiry. Please try again or WhatsApp us directly.') });
    } finally {
      setLoading(false);
      if (!waWindow) window.location.href = buildWhatsApp(form);
    }
  }, [form]);

  return (
    <form data-testid="enquiry-form" onSubmit={submit} className="grid md:grid-cols-2 gap-x-10 gap-y-7">
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={change('website')} className="absolute -left-[9999px] w-px h-px opacity-0" />
      <Field label="Your Name" value={form.name} onChange={change('name')} testId="enquiry-name-input" />
      <Field label="Partner's Name" value={form.partner} onChange={change('partner')} testId="enquiry-partner-input" />
      <Field label="Email" type="email" value={form.email} onChange={change('email')} testId="enquiry-email-input" />
      <Field label="Phone / WhatsApp" value={form.phone} onChange={change('phone')} testId="enquiry-phone-input" />
      {!compact && <Field label="Where do you currently live?" value={form.city} onChange={change('city')} testId="enquiry-city-input" />}
      {!compact && <Field label="Preferred Wedding Date" value={form.date} onChange={change('date')} testId="enquiry-date-input" />}
      {!compact && <Field label="Estimated Guest Count" type="number" value={form.guests} onChange={change('guests')} testId="enquiry-guests-input" />}
      {!compact && <Field label="Wedding Budget Range" value={form.budget} onChange={change('budget')} testId="enquiry-budget-input" />}
      {!compact && <Field label="Destination in mind?" value={form.destination} onChange={change('destination')} testId="enquiry-destination-input" />}
      {!compact && <Field label="Planning support required" value={form.support} onChange={change('support')} testId="enquiry-support-input" />}
      <div className="md:col-span-2">
        <Field label="Tell us about your wedding" value={form.message} onChange={change('message')} testId="enquiry-message-input" textarea />
      </div>
      <div className="md:col-span-2 flex flex-col md:flex-row md:items-center justify-between gap-6 pt-2">
        <p className="text-xs text-[#6b7c80] max-w-md">Your information is used only to understand and respond to your wedding enquiry in accordance with our privacy policy.</p>
        <button type="submit" disabled={loading} data-testid="enquiry-submit-button" className="btn-gold group">
          {loading ? 'Sending…' : "Let's Start Planning"}<ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
        </button>
      </div>
    </form>
  );
}
